import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import Field from '../components/Field';
import AuthBanner from '../components/auth/AuthBanner';
import ConsentBanner from '../components/ConsentBanner';
import LegalLinks from '../components/LegalLinks';
import MarketingPanel from '../components/auth/MarketingPanel';
import SetupRequired from '../components/auth/SetupRequired';
import ResetPasswordPanel from '../components/auth/ResetPasswordPanel';
import GoogleButton from '../components/auth/GoogleButton';
import authErrorView from '../components/auth/authErrorView';
import { useAuth } from '../context/AuthContext';
import { classifyError, classifyPasswordSignIn } from '../domain/authErrors';
import { CLIENT_EVENTS } from '../domain/clientEvents';
import { reportClientEvent } from '../lib/clientEvents';
import { signUpsInThisPage } from '../lib/adConversions';
import { BRAND } from '../domain/brand';
import { TRIAL_DAYS } from '../domain/billingPlans';
import s from '../components/auth/login.module.css';

// Sign in and sign up — one form, two modes.
//
// The error handling is the part with rules in it, and it lives elsewhere:
// domain/authErrors turns a Firebase code into a kind, and authErrorView turns
// a kind into the three slots this form renders.
//
// Google sits above the form in both modes (#567), because for Google signing
// in and signing up are one button: an address Firebase has not seen is a new
// account. What a new account is given — the sign-up counted, its first two
// documents — is AuthContext's, for both doors, so neither this page nor the
// way back from a Google redirect has to remember it.
//
// An email/password account then sees the check-your-email screen, not the
// app (#566): `SignInGate` in App.jsx replaces this page as soon as Firebase
// publishes the new account.
//
// The way to the register mode names the trial rather than saying "Sign up"
// (#723), and the register mode's lead says what the trial asks of a teacher:
// no money and no card. TRIAL_DAYS is the trial (domain/billingPlans.js), which
// ends in a paywall rather than a charge. Every trial link on the marketing
// site arrives in the register mode (#724, `SIGN_UP_PARAM` below).

const COPY = {
  login: {
    title: `Sign in to ${BRAND.name}`,
    lead: 'Welcome back. Enter your details below.',
    submit: 'Sign in',
    switchPrompt: `New to ${BRAND.name}?`,
    switchAction: `Start your ${TRIAL_DAYS}-day free trial`,
  },
  register: {
    title: 'Create your account',
    lead: `Your first ${TRIAL_DAYS} days are free, and you don't need a card to start.`,
    submit: 'Start my free trial',
    switchPrompt: 'Already have an account?',
    switchAction: 'Sign in',
  },
};

// The sign-up deep link (#724). Every trial link on the marketing site is
// /app/login?mode=register (the build points it at the app's own host, path
// and query intact: pointAppLinks in scripts/marketing-partials.mjs), so a new
// visitor opens this form ready to register. The /login route rather than the
// root, because RequireAuth's redirect to /login carries no query string; and
// /login is PublicOnly in App.jsx, so a visitor already signed in goes into
// the app. The param chooses the first mode and nothing else: registering
// still verifies the address (AuthContext `register`, SignInGate). It is read
// once and dropped from the address, so a reload opens the default sign-in
// mode.
const SIGN_UP_PARAM = { name: 'mode', value: 'register' };

function firstMode(search) {
  return new URLSearchParams(search).get(SIGN_UP_PARAM.name) === SIGN_UP_PARAM.value ? 'register' : 'login';
}

export default function LoginPage() {
  const {
    login, register, signInWithGoogle, signInMethodsFor, redirectError, clearRedirectError, resetPassword,
    isConfigured,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState(() => firstMode(location.search));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState(null);
  // The Google credential from an "already has an account" refusal, linked
  // once the password is accepted (AuthContext `login`).
  const [pendingGoogle, setPendingGoogle] = useState(null);

  // A Google refusal on the pop-up and one that came back from a redirect are
  // the same refusal, so both are handled here.
  // Only setters and module functions inside, so it is made once.
  const googleRefused = useCallback((err) => {
    const classified = classifyError(err);
    if (classified.kind === 'dismissed') return;
    if (classified.kind === 'googleAccountExists') {
      // Fill in the address and turn the form to Sign in, which is what the
      // banner tells the teacher to do.
      if (classified.email) setEmail(classified.email);
      setMode('login');
      setPendingGoogle(err?.pendingCredential || null);
    }
    setError(classified);
    reportClientEvent(CLIENT_EVENTS.signInFailed, { kind: classified.kind, code: err?.code, mode: 'google' });
  }, []);

  // Drop the deep link's param once it has been read (see SIGN_UP_PARAM).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has(SIGN_UP_PARAM.name)) return;
    params.delete(SIGN_UP_PARAM.name);
    const rest = params.toString();
    navigate({ search: rest ? `?${rest}` : '', hash: location.hash }, { replace: true });
  }, [location.search, location.hash, navigate]);

  useEffect(() => {
    if (!redirectError) return;
    googleRefused(redirectError);
    clearRedirectError();
  }, [redirectError, clearRedirectError, googleRefused]);

  if (!isConfigured) return <SetupRequired />;

  const copy = COPY[mode];
  const switchTo = (next) => { setMode(next); setError(null); };
  const view = authErrorView(error, mode, () => switchTo('login'), () => switchTo('reset'));

  const handleGoogle = async () => {
    setGoogleLoading(true);
    setError(null);
    try {
      // Null when the page is about to leave for Google's redirect; the
      // outcome then arrives on the next load. A new account is on its way
      // to /welcome (useThankYouPages), and is not sent Home over it.
      const before = signUpsInThisPage();
      const cred = await signInWithGoogle();
      if (cred && signUpsInThisPage() === before) navigate('/');
    } catch (err) {
      googleRefused(err);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    setError(null);
    try {
      if (mode === 'login') {
        await login(email, password, pendingGoogle);
        setPendingGoogle(null);
        navigate('/calendar');
        return;
      }
      // The first-run document and the Region are written inside `register`
      // (AuthContext `welcomeNewAccount`), as they are for a Google sign-up.
      await register(email, password);
      // No navigation here. A new account goes to /welcome, the sign-up's
      // thank-you page (#754), and src/hooks/useThankYouPages.js sends it there
      // once the auth observer has published the user — which can be after this
      // call resolves, and /welcome is behind RequireAuth, so going from here
      // could bounce through /login and on to Home.
    } catch (err) {
      let classified = classifyError(err);
      // A refused password may be an account that now signs in only with
      // Google (#591). Firebase's code is the same either way, so ask it
      // about the address; an answer it will not give leaves the generic one.
      if (mode === 'login' && classified.kind === 'wrongCreds') {
        classified = classifyPasswordSignIn(err, await signInMethodsFor(email));
      }
      setError(classified);
      // The funnel's first step, from the only place that can see it (#459):
      // the kind and Firebase's code, never the address that was typed.
      reportClientEvent(CLIENT_EVENTS.signInFailed, { kind: classified.kind, code: err?.code, mode });
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'reset') {
    return (
      <div className={s.split}>
        <MarketingPanel />
        <div className={s.formPane}>
          <ResetPasswordPanel
            email={email}
            onEmailChange={setEmail}
            onBack={() => switchTo('login')}
            onSend={resetPassword}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={s.split}>
      <MarketingPanel />

      <div className={s.formPane}>
        <div className={s.formWrap}>
          <div className={s.formHead}>
            <div className={s.markMedium} />
            <h1 className={s.formTitle}>{copy.title}</h1>
            <p className={s.formLead}>{copy.lead}</p>
          </div>

          <form onSubmit={handleSubmit} className={s.form}>
            {view.banner ? <AuthBanner title={view.banner.title} body={view.banner.body} /> : null}

            <GoogleButton loading={googleLoading} disabled={loading} onClick={handleGoogle} />
            <div className={s.or} role="separator">or</div>

            <Field label="Email address" htmlFor="email" required error={view.emailError}>
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@studio.com"
                value={email}
                data-error={view.emailError ? 'true' : undefined}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </Field>

            <Field label="Password" htmlFor="password" required error={view.passwordError}>
              <input
                id="password"
                type="password"
                autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                placeholder="••••••••"
                value={password}
                data-error={view.passwordError ? 'true' : undefined}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>

            {mode === 'login' ? (
              // On the form, not only inside a failed-sign-in message, so
              // someone who knows they have forgotten it need not guess wrong
              // first.
              <button type="button" className={s.forgot} onClick={() => switchTo('reset')}>
                Forgot your password?
              </button>
            ) : null}

            <Button type="submit" variant="primary" size="lg" loading={loading} className={s.submit}>
              {copy.submit}
            </Button>
          </form>

          <div className={s.switch}>
            {copy.switchPrompt}{' '}
            <button
              className={s.switchButton}
              onClick={() => switchTo(mode === 'login' ? 'register' : 'login')}
            >
              {copy.switchAction}
            </button>
          </div>

          {/* Under the form, so somebody signing in reaches the form first;
              it renders nothing once answered here or on the marketing site,
              and LegalLinks' Cookie settings opens it again (#755). */}
          <ConsentBanner inline />

          {/* Privacy, Terms and Cookie settings, last and smallest, in both
              modes. Here rather than in MarketingPanel, which a phone-width
              screen hides. */}
          <LegalLinks cookieSettings />
        </div>
      </div>
    </div>
  );
}
